// Manual (non-TMDB) titles — the shows TMDB has never catalogued, like
// "Big Boss". The server owns the listing (see library-view.js / library-cards.js):
// it joins a title's record with the episode links that can actually play, so
// `seasons` here is exactly what has a source — {"8": [1, 2, 14]}.
//
// A title with no playable episode is not returned at all, which is why a row
// can simply be rendered from this list without any empty-state handling.

/** "S08E01" — two-digit season and episode, whichever numbers the show uses. */
export function episodeLabel(season, episode) {
  return `S${String(season).padStart(2, "0")}E${String(episode).padStart(2, "0")}`;
}

/** The record's own artwork, else the poster the server generates for it. */
export function libraryPosterUrl(record) {
  if (!record) return null;
  return record.posterUrl || `/api/library/poster?id=${record.id}`;
}

function rowOf(record) {
  if (!record || record.contentType !== "TV") return null;
  const seasons = record.seasons && typeof record.seasons === "object" ? record.seasons : {};
  const episodes = [];
  for (const season of Object.keys(seasons).sort((a, b) => Number(a) - Number(b))) {
    for (const episode of [...(seasons[season] || [])].sort((a, b) => a - b)) {
      episodes.push({ season: Number(season), episode: Number(episode) });
    }
  }
  if (episodes.length === 0) return null;
  return {
    id: `manual-${record.id}`,
    tmdbId: Number(record.id),
    title: record.title,
    language: record.language || null,
    languageCode: record.languageCode || null,
    posterUrl: libraryPosterUrl(record),
    // 16:9 artwork (for a landscape banner) and a live stream, when the title
    // has either — see upsertLibraryTitle.
    landscape: record.landscape === true,
    liveUrl: record.liveUrl || null,
    episodes,
  };
}

/** Every manual TV title that has playable episodes, newest added first. */
export async function fetchLibraryRows() {
  try {
    const res = await fetch("/api/library/list", { headers: { accept: "application/json" } });
    if (!res.ok) return [];
    const data = await res.json();
    return (Array.isArray(data?.titles) ? data.titles : []).map(rowOf).filter(Boolean);
  } catch {
    // The library is a bonus row: a failure must never take the home page down.
    return [];
  }
}

/**
 * The reserved slot a show's live stream plays through: season 0, episode 0.
 * No real episode can be season 0, so the live link can never collide with one,
 * and the card does not have to borrow an episode number it would then have to
 * display.
 */
const LIVE_SLOT = { season: 0, episode: 0 };

/** The show's live stream as a card — the first thing in the row. */
function liveCard(row) {
  const params = new URLSearchParams({
    type: "episode",
    id: String(row.tmdbId),
    title: row.title,
    season: String(LIVE_SLOT.season),
    episode: String(LIVE_SLOT.episode),
  });
  if (row.posterUrl) params.set("poster", row.posterUrl);
  if (row.languageCode) params.set("lang", row.languageCode);
  return {
    key: `${row.id}-live`,
    tmdbId: row.tmdbId,
    contentType: "TV_SHOW",
    title: row.title,
    posterPath: row.posterUrl,
    live: true,
    episodeLabel: "LIVE",
    language: row.language,
    playUrl: `/watch?${params.toString()}`,
  };
}

/**
 * One card per playable episode, in the shape PosterCard already renders.
 * `playUrl` replaces the detail route (there is no TMDB page for a manual
 * title) and `episodeLabel`/`language` replace the year/type line, so the card
 * reads: image, "Big Boss", "S08E01", "Malayalam" — and clicking plays it.
 */
export function episodeCards(row) {
  const cards = row.episodes.map(({ season, episode }) => {
    const poster = row.posterUrl;
    const params = new URLSearchParams({
      type: "episode",
      id: String(row.tmdbId),
      title: row.title,
      season: String(season),
      episode: String(episode),
    });
    if (poster) params.set("poster", poster);
    // The player picks the show's own audio out of a multi-language master.
    if (row.languageCode) params.set("lang", row.languageCode);
    return {
      key: `${row.id}-s${season}e${episode}`,
      tmdbId: row.tmdbId,
      contentType: "TV_SHOW",
      title: row.title,
      posterPath: poster,
      episodeLabel: episodeLabel(season, episode),
      language: row.language,
      playUrl: `/watch?${params.toString()}`,
    };
  });
  if (row.liveUrl) cards.unshift(liveCard(row));
  return cards;
}
